import { useState, useMemo } from 'react';
import { SortField, Order } from '../types';
import { getCurrentMonth } from '../utils';
import { useDebounce } from './useDebounce';

/** Получает username из localStorage — используется для фильтрации аптек на сервере */
const getCurrentUsername = (): string => localStorage.getItem('username') ?? '';

export interface JournalFilterState {
  // ── Raw filter state ──
  searchTerm: string;
  setSearchTerm: (v: string) => void;

  selectedMonth: string;
  setSelectedMonth: (v: string) => void;

  selectedPharmacy: string | null;
  setSelectedPharmacy: (v: string | null) => void;

  selectedCategory: string;
  setSelectedCategory: (v: string) => void;

  selectedManager: string;
  setSelectedManager: (v: string) => void;

  selectedProduct: string | null;
  setSelectedProduct: (v: string | null) => void;

  selectedSerial: string | null;
  setSelectedSerial: (v: string | null) => void;

  selectedProducer: string | null;
  setSelectedProducer: (v: string | null) => void;

  selectedPriceGroup: string | null;
  setSelectedPriceGroup: (v: string | null) => void;

  expiryFrom: string;
  setExpiryFrom: (v: string) => void;

  expiryTo: string;
  setExpiryTo: (v: string) => void;

  // ── Sort ──
  orderBy: SortField;
  order: Order;
  handleSort: (field: SortField) => void;

  // ── Pagination ──
  page: number;
  setPage: (v: number) => void;

  // ── Computed ──
  filterParams: Record<string, string>;

  // ── Actions ──
  handleReset: () => void;
}

/**
 * Управляет всеми состояниями фильтрации, сортировки и пагинации журнала.
 * Вычисляет `filterParams` — объект query-параметров для API-запросов.
 */
export function useJournalFilters(): JournalFilterState {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [selectedPharmacy, setSelectedPharmacy] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedManager, setSelectedManager] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [selectedSerial, setSelectedSerial] = useState<string | null>(null);
  const [selectedProducer, setSelectedProducer] = useState<string | null>(null);
  const [selectedPriceGroup, setSelectedPriceGroup] = useState<string | null>(null);
  const [expiryFrom, setExpiryFrom] = useState('');
  const [expiryTo, setExpiryTo] = useState('');

  const [orderBy, setOrderBy] = useState<SortField>('recordDate');
  const [order, setOrder] = useState<Order>('desc');
  const [page, setPage] = useState(1);

  // Дебаунс поиска — 400мс, чтобы не слать запрос на каждый символ
  const debouncedSearch = useDebounce(searchTerm, 400);

  const filterParams = useMemo(() => {
    const p: Record<string, string> = {};
    // — Передаём username на бэкенд для фильтрации по привязанным аптекам —
    const username = getCurrentUsername();
    if (username) p.username = username;
    if (selectedMonth && selectedMonth !== 'all') p.month = selectedMonth;
    if (selectedPharmacy) p.pharmacyNum = selectedPharmacy;
    if (selectedCategory !== 'all') p.category = selectedCategory;
    if (selectedManager !== 'all') p.manager = selectedManager;
    if (selectedProduct) p.materialName = selectedProduct;
    if (selectedSerial) p.serialNumber = selectedSerial;
    if (selectedProducer) p.producer = selectedProducer;
    if (selectedPriceGroup) p.priceGroup = selectedPriceGroup;
    if (expiryFrom) p.expiryFrom = expiryFrom;
    if (expiryTo) p.expiryTo = expiryTo;
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    return p;
  }, [
    selectedMonth, selectedPharmacy, selectedCategory, selectedManager,
    selectedProduct, selectedSerial, selectedProducer, selectedPriceGroup,
    expiryFrom, expiryTo, debouncedSearch,
  ]);

  const handleSort = (field: SortField) => {
    setOrder(orderBy === field && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(field);
    setPage(1);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedPharmacy(null);
    setSelectedCategory('all');
    setSelectedManager('all');
    setSelectedProduct(null);
    setSelectedSerial(null);
    setSelectedProducer(null);
    setSelectedPriceGroup(null);
    setExpiryFrom('');
    setExpiryTo('');
    setSelectedMonth(getCurrentMonth());
    setPage(1);
  };

  return {
    searchTerm, setSearchTerm,
    selectedMonth, setSelectedMonth,
    selectedPharmacy, setSelectedPharmacy,
    selectedCategory, setSelectedCategory,
    selectedManager, setSelectedManager,
    selectedProduct, setSelectedProduct,
    selectedSerial, setSelectedSerial,
    selectedProducer, setSelectedProducer,
    selectedPriceGroup, setSelectedPriceGroup,
    expiryFrom, setExpiryFrom,
    expiryTo, setExpiryTo,
    orderBy, order, handleSort,
    page, setPage,
    filterParams,
    handleReset,
  };
}
